import { QueryClient, useQuery } from "@tanstack/react-query";

import collabApi from "api/collaboration";
import { queryKeys } from "api/query-keys";
import {
  ColCollaborator,
  ColComment,
  ColComments,
  ColCommentVisibility,
} from "./types";

export interface CollaborationUnread {
  channelId: string;
  unreads: number;
  mentioned: boolean;
}

function getScopedChannelIds(channelIds: string[] = []) {
  return [...new Set(channelIds.filter(Boolean))].sort();
}

const activeChannelCounts = new Map<string, number>();
const locallyReadBaselines = new Map<string, number>();
const localUnreadDeltas = new Map<string, number>();
const LOCAL_READ_SUPPRESSION_MS = 5000;

function isLocalReadSuppressionActive(channelId: string) {
  const baseline = locallyReadBaselines.get(channelId);
  if (baseline === undefined) return false;
  if (Date.now() < baseline) return true;
  locallyReadBaselines.delete(channelId);
  localUnreadDeltas.delete(channelId);
  return false;
}

function getDisplayedUnread(channel: CollaborationUnread): CollaborationUnread {
  const localDelta = localUnreadDeltas.get(channel.channelId) ?? 0;
  if (isLocalReadSuppressionActive(channel.channelId)) {
    return { ...channel, unreads: localDelta };
  }
  return {
    ...channel,
    unreads: channel.unreads,
  };
}

function normalizeCollaborationUnreads(
  unreads: CollaborationUnread[]
): CollaborationUnread[] {
  const normalized = unreads.map((unread) => getDisplayedUnread(unread));
  for (const [channelId, unreadCount] of localUnreadDeltas) {
    if (!isLocalReadSuppressionActive(channelId)) continue;
    if (normalized.some((channel) => channel.channelId === channelId)) continue;
    normalized.push({ channelId, unreads: unreadCount, mentioned: false });
  }
  return normalized;
}

export async function fetchCollaborationComments(
  channelId: string
): Promise<ColComments> {
  const { data } = await collabApi.getComments(channelId);
  return data;
}

export async function fetchCollaborationCollaborators(): Promise<
  ColCollaborator[]
> {
  const { data } = await collabApi.getCollaborators();
  return data;
}

export async function addCollaborationChatComment(
  channelId: string,
  payload: {
    body: string;
    metadata: {
      post: { postId: string };
      postType: string;
    };
    visibility: ColCommentVisibility;
  }
) {
  await collabApi.addComment(channelId, payload);
}

export async function editCollaborationChatComment(
  channelId: string,
  commentId: string,
  payload: { body: string; visibility: ColCommentVisibility }
) {
  await collabApi.editComment(channelId, commentId, payload);
}

export async function deleteCollaborationChatComment(
  channelId: string,
  commentId: string
) {
  await collabApi.deleteComment(channelId, commentId);
}

export async function fetchCollaborationUnreads(
  channelIds: string[] = []
): Promise<CollaborationUnread[]> {
  const scopedChannelIds = getScopedChannelIds(channelIds);
  if (scopedChannelIds.length === 0) {
    return [];
  }

  const { data } = await collabApi.getUnreads(scopedChannelIds);
  return normalizeCollaborationUnreads(data);
}

export function useCollaborationComments(channelId: string | null) {
  return useQuery({
    queryKey: channelId
      ? queryKeys.collaboration.comments(channelId)
      : ["collaboration", "comments", "none"],
    queryFn: () => fetchCollaborationComments(channelId as string),
    enabled: !!channelId,
  });
}

export function useCollaborationCollaborators() {
  return useQuery({
    queryKey: queryKeys.collaboration.collaborators,
    queryFn: fetchCollaborationCollaborators,
  });
}

export function useCollaborationUnreads(channelIds: string[] = []) {
  const scopedChannelIds = getScopedChannelIds(channelIds);

  return useQuery({
    queryKey: queryKeys.collaboration.unreads(scopedChannelIds),
    queryFn: () => fetchCollaborationUnreads(scopedChannelIds),
    enabled: scopedChannelIds.length > 0,
    initialData: [],
  });
}

export function markCollaborationChannelReadInCache(
  queryClient: QueryClient,
  channelId: string
) {
  queryClient.setQueriesData<CollaborationUnread[]>(
    { queryKey: queryKeys.collaboration.unreadsBase },
    (unreads = []) => {
      locallyReadBaselines.set(
        channelId,
        Date.now() + LOCAL_READ_SUPPRESSION_MS
      );
      localUnreadDeltas.set(channelId, 0);
      return unreads.map((channel) =>
        channel.channelId === channelId
          ? { ...channel, unreads: 0, mentioned: false }
          : channel
      );
    }
  );
}

export function incrementCollaborationUnreadInCache(
  queryClient: QueryClient,
  channelId: string
) {
  if (isCollaborationChannelActive(channelId)) {
    markCollaborationChannelReadInCache(queryClient, channelId);
    return;
  }

  localUnreadDeltas.set(channelId, (localUnreadDeltas.get(channelId) ?? 0) + 1);

  queryClient.setQueriesData<CollaborationUnread[]>(
    { queryKey: queryKeys.collaboration.unreadsBase },
    (unreads = []) => {
      const existing = unreads.find(
        (channel) => channel.channelId === channelId
      );
      if (!existing) {
        return [...unreads, { channelId, unreads: 1, mentioned: false }];
      }
      return unreads.map((channel) =>
        channel.channelId === channelId
          ? { ...channel, unreads: channel.unreads + 1 }
          : channel
      );
    }
  );
}

export function registerActiveCollaborationChannel(channelId: string) {
  activeChannelCounts.set(
    channelId,
    (activeChannelCounts.get(channelId) ?? 0) + 1
  );
}

export function unregisterActiveCollaborationChannel(channelId: string) {
  const count = activeChannelCounts.get(channelId) ?? 0;
  if (count <= 1) {
    activeChannelCounts.delete(channelId);
    return;
  }
  activeChannelCounts.set(channelId, count - 1);
}

export function isCollaborationChannelActive(channelId: string) {
  return activeChannelCounts.has(channelId);
}

export function resetCollaborationQueryStateForTests() {
  activeChannelCounts.clear();
  locallyReadBaselines.clear();
  localUnreadDeltas.clear();
}

export function upsertCollaborationCommentInCache(
  queryClient: QueryClient,
  channelId: string,
  comment: ColComment,
  activeUserId?: string
) {
  const commentForCache =
    activeUserId && !comment.readBy.includes(activeUserId)
      ? { ...comment, readBy: [...comment.readBy, activeUserId] }
      : comment;

  queryClient.setQueryData<ColComments>(
    queryKeys.collaboration.comments(channelId),
    (data) => {
      const current = data ?? { channelId, activeThread: [], legacyThread: [] };
      const activeThread = current.activeThread ?? [];
      const legacyThread = current.legacyThread ?? [];
      const existsInActive = activeThread.some(
        (item) => item._id === commentForCache._id
      );
      const existsInLegacy = legacyThread.some(
        (item) => item._id === commentForCache._id
      );
      return {
        ...current,
        activeThread: existsInActive
          ? activeThread.map((item) =>
              item._id === commentForCache._id
                ? { ...item, ...commentForCache }
                : item
            )
          : existsInLegacy
            ? activeThread
            : [...activeThread, commentForCache],
        legacyThread: existsInLegacy
          ? legacyThread.map((item) =>
              item._id === commentForCache._id
                ? { ...item, ...commentForCache }
                : item
            )
          : legacyThread,
      };
    }
  );
}
